import { adminConflicts, invasionPolicies } from '../data/world/invasionPolicies'
import { nameKey } from './worldFormat'
import { bagOrigins, bagsOpenedBy } from './bagSources'
import { effectiveRouteQuality } from './rewardQuality'
import { adminConfirmation, formatAdminDate } from '../data/guide/adminConfirmations'
import { EXPORT_DATE_LABEL } from '../data/world/package'
import type { InvasionPolicy, ItemCategory } from '../data/world/invasionPolicies'
import type { DropRoute, RouteTarget, WorldData } from '../types/world'

/*
 * Validación compartida: contrasta cada ruta exportada que relaciona un ítem con una invasión
 * con la política aprobada. La usan las fichas de ítem, de monstruo, el diagnóstico y el buscador
 * (a través de las fichas). Las rutas no autorizadas se retienen para los jugadores y se
 * conservan en el panel técnico.
 *
 * Recompensas indirectas (p. ej. Dorados): monstruo → caja que entrega → posibles resultados al abrirla.
 * Si el monstruo entrega una caja autorizada y esa caja contiene el ítem, el ítem se autoriza como
 * recompensa INDIRECTA (requiere abrir la caja); no se exige que figure como drop directo del monstruo.
 *
 * Calidad: una calidad vacía en la ruta se resuelve desde los perfiles de opciones (utils/rewardQuality);
 * sin perfil queda "no documentada", nunca "normal" por defecto.
 */

export type InvasionVerdict = 'authorized' | 'conflict' | 'unlisted'

export type InvasionCheck = {
  policy: InvasionPolicy
  verdict: InvasionVerdict
  reason: string
  /** Caja por la que se obtiene el ítem (recompensa indirecta). */
  indirectVia?: { boxKey: string; boxName: string }
  /** Tres estados independientes: configuración, vigencia actual y prueba en el juego. */
  states: RewardStates
}

export type RewardStates = { config: string; validity: string; inGame: string }

/** Estados separados: una relación documentada sin confirmación reciente NO es un error entre archivos. */
function rewardStates(p: InvasionPolicy): RewardStates {
  const admin = adminConfirmation(`invasion-validity:${p.guideEntryId}`)
  return {
    config: `Recompensa documentada en la configuración del ${EXPORT_DATE_LABEL}`,
    validity: admin ? `Vigencia confirmada por administración (${formatAdminDate(admin.date)})` : 'Vigencia actual pendiente de confirmar por administración',
    inGame: 'No probada dentro del juego',
  }
}

function evaluate(w: WorldData, r: DropRoute, p: InvasionPolicy): InvasionCheck {
  return { ...evaluateCore(w, r, p), states: rewardStates(p) }
}

const cache = new WeakMap<WorldData, Map<string, InvasionCheck[]>>()

export function itemCategory(section: number | undefined, name: string): ItemCategory {
  if (/^jewel of /i.test(name) || /^gemstone$/i.test(name)) return 'jewel'
  if (section === undefined) return 'other'
  if (section >= 0 && section <= 6) return 'weapon'
  if (section >= 7 && section <= 11) return 'set'
  return 'other'
}

const categoryLabel: Record<ItemCategory, string> = { weapon: 'arma', set: 'pieza de set', jewel: 'joya', other: 'otro tipo de ítem' }

function policyMatchesTarget(p: InvasionPolicy, t: { eventId?: string; monsterId?: number }): boolean {
  if (t.eventId) return p.eventIds.includes(t.eventId)
  return t.monsterId !== undefined && p.monsterIds.includes(t.monsterId)
}

/** Políticas de invasión que afectan a una ruta (por bag_id, event_id o monster_id confirmados). */
export function policiesForRoute(w: WorldData, r: DropRoute): InvasionPolicy[] {
  const targets = w.idx.routeTargetsByRoute.get(r.id) ?? []
  return invasionPolicies.filter((p) => (r.bagId !== undefined && p.bagIds.includes(r.bagId)) || targets.some((t) => policyMatchesTarget(p, t)))
}

const nameOf = (w: WorldData, key: string) => w.variants.get(key)?.name ?? w.items.get(key)?.name ?? key

/** ¿La clave (ítem o variante) es una recompensa autorizada expresamente por la política? */
function authorizedKey(w: WorldData, p: InvasionPolicy, key: string): boolean {
  const base = w.variants.get(key)?.itemId ?? key.split('@')[0]
  const names = [nameOf(w, key), w.items.get(base)?.name ?? base].map(nameKey)
  if (p.authorizedNames.some((n) => names.includes(nameKey(n)))) return true
  // Un ID base autorizado (p. ej. 14,11) solo cubre sus variantes nombradas en la política, si las hay.
  if (!p.authorizedItemIds?.includes(base)) return false
  return key === base || !w.variants.has(key) || p.authorizedNames.length === 0 || p.authorizedNames.some((n) => nameKey(n) === nameKey(nameOf(w, key)))
}

/** ¿Algún monstruo/evento de la política entrega esa caja? (ruta exportada o premio directo de evento) */
function policyGivesBox(w: WorldData, p: InvasionPolicy, boxKey: string): boolean {
  const base = w.variants.get(boxKey)?.itemId ?? boxKey.split('@')[0]
  const level = w.variants.get(boxKey)?.level
  const routes = [...(w.idx.routesByVariant.get(boxKey) ?? []), ...(boxKey === base ? w.idx.routesByItem.get(base) ?? [] : [])]
  if (routes.some((br) => (w.idx.routeTargetsByRoute.get(br.id) ?? []).some((t) => !t.isIndirect && policyMatchesTarget(p, t)))) return true
  return (w.idx.eventItemsByItem.get(base) ?? []).some((e) => e.active !== false && (level === undefined || e.itemLevel === undefined || e.itemLevel === level) && policyMatchesTarget(p, e))
}

/** ¿La caja contiene el ítem? (bolsas que abre la caja → 21_bag_rewards) */
function boxContains(w: WorldData, boxKey: string, itemId: string): boolean {
  return bagsOpenedBy(w, boxKey).some((b) => w.bagRewards.some((x) => x.bagId === b.id && x.itemId === itemId && x.usedByRuntime !== false))
}

/** Cadena monstruo → caja → resultado documentada en ambos tramos. */
function indirectChain(w: WorldData, r: DropRoute, p: InvasionPolicy, itemId: string): InvasionCheck['indirectVia'] {
  const boxes = new Set<string>(r.viaItemIds)
  if (r.bagId) bagOrigins(w, r.bagId).forEach((o) => o.kind === 'open' && boxes.add(o.itemKey))
  for (const boxKey of boxes) {
    if (authorizedKey(w, p, boxKey) && policyGivesBox(w, p, boxKey) && boxContains(w, boxKey, itemId)) return { boxKey, boxName: nameOf(w, boxKey) }
  }
  return undefined
}

function evaluateCore(w: WorldData, r: DropRoute, p: InvasionPolicy): Omit<InvasionCheck, 'states'> {
  const itemId = r.itemId ?? r.variantId?.split('@')[0] ?? ''
  const item = w.items.get(itemId)
  const variant = r.variantId ? w.variants.get(r.variantId) : undefined
  const name = variant?.name ?? item?.name ?? itemId
  const baseName = item?.name ?? name
  const eq = effectiveRouteQuality(w, r)
  const isExc = eq.quality === 'excellent' || eq.quality === 'ancient'
  const qualityNote = eq.source === 'perfil' ? ` ${eq.note}` : ''
  const qName = r.quality || (eq.source === 'perfil' ? `${eq.quality} (según perfil)` : eq.quality)

  const admin = adminConflicts.find((c) => c.guideEntryId === p.guideEntryId && c.itemId === itemId)
  if (admin) return { policy: p, verdict: 'conflict', reason: admin.note }

  if (p.authorizedItemIds?.includes(itemId) || p.authorizedNames.some((n) => nameKey(n) === nameKey(baseName) || nameKey(n) === nameKey(name))) {
    if (isExc && p.forbidExcellent) return { policy: p, verdict: 'conflict', reason: `Versión ${qName}: la información aprobada excluye ítems Excellent/Ancient.${qualityNote}` }
    return { policy: p, verdict: 'authorized', reason: `Nombrado expresamente en la información aprobada: ${p.approved}` }
  }

  // Recompensa indirecta: el monstruo entrega una caja autorizada y la caja contiene el ítem.
  const via = indirectChain(w, r, p, itemId)
  if (via) {
    if (isExc && p.forbidExcellent) return { policy: p, verdict: 'conflict', reason: `Versión ${qName} dentro de ${via.boxName}: la información aprobada excluye ítems Excellent/Ancient.${qualityNote}`, indirectVia: via }
    return {
      policy: p,
      verdict: 'authorized',
      reason: `Recompensa indirecta: ${p.name} entrega ${via.boxName} y al abrirla puede salir este ítem. Requiere abrir la caja; no es un drop directo del monstruo.`,
      indirectVia: via,
    }
  }

  // Sin ficha editorial aprobada: pendiente de aprobación, no contradicción entre fuentes.
  if (!p.generalCategories.length && !p.authorizedNames.length) {
    return { policy: p, verdict: 'unlisted', reason: 'Pendiente de aprobación editorial: la invasión aún no tiene ficha aprobada en la guía. Los datos exportados existen y se conservan.' }
  }

  if (isExc && p.forbidExcellent) return { policy: p, verdict: 'conflict', reason: `Versión ${qName}: la información aprobada indica NO Excellent.${qualityNote}` }

  const cat = itemCategory(item?.section, baseName)
  if (!p.generalCategories.includes(cat)) {
    return {
      policy: p,
      verdict: 'conflict',
      reason: p.generalCategories.length || p.authorizedNames.length
        ? `${categoryLabel[cat]} no incluido en las recompensas aprobadas (${p.approved})`
        : 'La invasión no tiene información aprobada en la guía.',
    }
  }
  if (p.excellentOnlyGeneral && cat !== 'jewel' && !isExc) {
    if (eq.source === 'sin documentar') {
      return { policy: p, verdict: 'unlisted', reason: `Calidad no documentada: la ruta no declara calidad y no hay perfil de opciones que la resuelva. Falta: perfil de opciones (excellent_count) de esta recompensa y ${p.missing ?? 'lista vigente de ítems'}` }
    }
    return { policy: p, verdict: 'conflict', reason: `La información aprobada solo contempla un ítem Excellent adicional del jefe; esta versión no es Excellent (${eq.note}).` }
  }
  const lvl = variant?.level
  if (p.levels?.length && lvl !== undefined && !p.levels.includes(lvl)) {
    return { policy: p, verdict: 'conflict', reason: `Nivel +${lvl} fuera de lo aprobado (+${p.levels.join('/+')}).` }
  }
  return {
    policy: p,
    verdict: 'unlisted',
    reason: `Relación documentada en la configuración (${categoryLabel[cat]}${isExc ? `, ${qName}` : ''}). Retenida porque falta confirmar su vigencia actual por administración, no porque falten datos ni por un error entre archivos. Falta: ${p.missing ?? 'lista vigente de ítems'}.${qualityNote}`,
  }
}

/** Resultado de todas las políticas para una ruta. Vacío = la ruta no es de invasión. */
export function invasionChecks(w: WorldData, r: DropRoute): InvasionCheck[] {
  let m = cache.get(w)
  if (!m) {
    m = new Map()
    cache.set(w, m)
  }
  const hit = m.get(r.id)
  if (hit) return hit
  const out = policiesForRoute(w, r).map((p) => evaluate(w, r, p))
  m.set(r.id, out)
  return out
}

/** Políticas NO autorizadas para la ruta: sus objetivos se retienen para los jugadores. */
export function withheldPolicies(w: WorldData, r: DropRoute): InvasionCheck[] {
  return invasionChecks(w, r).filter((c) => c.verdict !== 'authorized')
}

/** ¿Este objetivo pertenece a una invasión cuya asociación está retenida? */
export function targetWithheld(w: WorldData, r: DropRoute, t: { eventId?: string; monsterId?: number }): boolean {
  const held = withheldPolicies(w, r)
  if (!held.length) return false
  if (held.some((c) => r.bagId !== undefined && c.policy.bagIds.includes(r.bagId))) return true
  return held.some((c) => (t.eventId ? c.policy.eventIds.includes(t.eventId) : t.monsterId !== undefined && c.policy.monsterIds.includes(t.monsterId)))
}

export type InvasionAudit = {
  policy: InvasionPolicy
  routes: number
  items: number
  authorized: number
  unlisted: number
  conflict: number
  /** Autorizadas como recompensa indirecta (requieren abrir una caja). */
  indirect: number
  rows: { routeId: string; itemId: string; name: string; quality?: string; verdict: InvasionVerdict; reason: string; via?: string }[]
}

/** Auditoría completa para el panel técnico. */
export function auditInvasions(w: WorldData): InvasionAudit[] {
  const by = new Map<string, InvasionAudit>()
  invasionPolicies.forEach((p) => by.set(p.guideEntryId, { policy: p, routes: 0, items: 0, authorized: 0, unlisted: 0, conflict: 0, indirect: 0, rows: [] }))
  w.routes.forEach((r) => {
    invasionChecks(w, r).forEach((c) => {
      const a = by.get(c.policy.guideEntryId)!
      a.routes++
      a[c.verdict]++
      if (c.verdict === 'authorized' && c.indirectVia) a.indirect++
      const id = r.variantId ?? r.itemId ?? ''
      const eq = effectiveRouteQuality(w, r)
      a.rows.push({
        routeId: r.id,
        itemId: id,
        name: w.variants.get(id)?.name ?? w.items.get(r.itemId ?? '')?.name ?? id,
        quality: r.quality || (eq.source === 'perfil' ? `${eq.quality} (perfil ${eq.profile?.profileKeys.join(', ')})` : undefined),
        verdict: c.verdict,
        reason: c.reason,
        via: c.indirectVia?.boxName,
      })
    })
  })
  by.forEach((a) => (a.items = new Set(a.rows.map((x) => x.itemId.split('@')[0])).size))
  return Array.from(by.values())
}
